import {
  Alert,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  TextField
} from '@mui/material';
import { UpdateTemplate, Template } from '@thro-types/notification';

import { useEffect, useLayoutEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useDispatch } from 'react-redux';

import { useTemplatesMutation, useTemplatesQuery } from 'api/hooks/notifications';
import DetailPage from 'app/components/DetailPage';
import InfoBox from 'app/components/InfoBox';
import VariablesDescription from 'app/components/MailTemplate/VariablesDescription';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { initialMailTemplateValues } from './validation';

interface MailTemplateProps {
  appId: string;
}

export const MailTemplate = ({ appId }: MailTemplateProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    data: templates,
    isLoading,
    isPending
  } = useTemplatesQuery({
    params: { applicationsId: appId }
  });

  const [selectedTemplate, setSelectedTemplate] = useState<Template | undefined>(undefined);

  const { update } = useTemplatesMutation({
    params: { generateText: true }
  });

  const { handleSubmit, register, setValue, control } = useForm({
    defaultValues: initialMailTemplateValues(selectedTemplate)
  });

  useEffect(() => {
    if (selectedTemplate) {
      setValue('subject', selectedTemplate.subject);
      setValue('html', selectedTemplate.html);
    }
  }, [selectedTemplate, setValue]);

  useLayoutEffect(() => {
    if (!!templates?.length) {
      setSelectedTemplate(templates[0]);
    }
  }, [templates]);

  const handleTemplateUpdate = async (values: UpdateTemplate) => {
    try {
      if (!selectedTemplate) return;

      await update.mutateAsync({
        id: selectedTemplate.id,
        data: {
          subject: values.subject,
          html: values.html
        }
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.articleMessagesSettings.alerts.templateSaveSuccessful())
        )
      );
    } catch (err) {
      dispatch(t(translations.pages.articleMessagesSettings.alerts.templateSaveFailed()));
    }
  };

  const handleTemplateChange = (e: SelectChangeEvent<number>) => {
    if (typeof e.target.value === 'string') return;
    const templateFromId = templates?.find(t => t.id === e.target.value);
    if (!templateFromId) return;
    setSelectedTemplate(templateFromId);
  };

  const renderContent = (template: Template | undefined) => {
    if (!template) {
      return (
        <Grid container>
          <Alert severity="error">
            {t(translations.pages.articleMessagesSettings.alerts.loadError())}
          </Alert>
        </Grid>
      );
    }
    return (
      <Grid item xs={12}>
        <form onSubmit={handleSubmit(handleTemplateUpdate)}>
          <Grid container spacing={2} justifyContent={'space-between'}>
            <Grid item xs={12} md={6}>
              <TextField
                {...register('subject')}
                className="article-messages-settings__content__subject"
                label={t(translations.common.subject())}
                variant="outlined"
                fullWidth
                size="small"
              />
              <Controller
                name="html"
                control={control}
                render={({ field }) => (
                  <ReactQuill
                    className="article-messages-settings__content__rte"
                    theme="snow"
                    modules={{
                      toolbar: [
                        ['bold', 'italic', 'link'],
                        [
                          { list: 'ordered' },
                          { list: 'bullet' },
                          { indent: '-1' },
                          { indent: '+1' }
                        ],
                        ['clean']
                      ]
                    }}
                    value={field.value}
                    onChange={field.onChange}
                    formats={['bold', 'italic', 'link', 'list', 'bullet', 'indent']}
                  />
                )}
              />
              <Grid container justifyContent="flex-end">
                <Button
                  className="article-messages-settings__content__save"
                  variant="contained"
                  type="submit"
                >
                  {t(translations.common.save())}
                </Button>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6} className="article-messages-settings__content__info">
              {template.variables && (
                <InfoBox
                  title={t(
                    translations.pages.articleMessagesSettings.templateDescription.heading()
                  )}
                  type="info"
                >
                  <Grid container className="article-messages-settings__content__info__description">
                    {/* TODO when notification deployed add description
                  {template.description} */}
                    {translations.pages.articleMessagesSettings.templateDescription[
                      template.name.replace('-', '_')
                    ]
                      ? `${t(
                          translations.pages.articleMessagesSettings.templateDescription[
                            template.name
                          ]()
                        )} `
                      : ''}
                    {t(translations.pages.articleMessagesSettings.templateDescription.content())}
                  </Grid>
                  <Grid
                    container
                    className="article-messages-settings__content__info__placeholders"
                  >
                    <VariablesDescription
                      ignoreTextScope
                      variables={template.variables}
                      i18nDictionary={translations.pages.articleMessagesSettings.templateVariables}
                    />
                  </Grid>
                </InfoBox>
              )}
            </Grid>
          </Grid>
        </form>
      </Grid>
    );
  };

  if (isLoading) {
    return (
      <DetailPage className="article-messages-settings__content">
        <Grid container justifyContent={'space-between'} marginTop={10}>
          <Skeleton width={'48%'} height={'22em'} sx={{ transform: 'unset' }} />
          <Skeleton width={'48%'} height={'22em'} sx={{ transform: 'unset' }} />
        </Grid>
      </DetailPage>
    );
  }

  if (templates?.length === 0 && !isPending) {
    return (
      <DetailPage className="article-messages-settings__content">
        <Grid container>
          <Alert severity="error">
            {t(translations.pages.articleMessagesSettings.alerts.noTemplatesFound())}
          </Alert>
        </Grid>
      </DetailPage>
    );
  }

  return (
    <DetailPage className="article-messages-settings__content">
      <Grid container gap={2}>
        <Grid container justifyContent={'space-between'}>
          <FormControl>
            <InputLabel id="demo-simple-select-label">
              {t(translations.pages.articleMessagesSettings.templateNameLabel())}
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              sx={{ minWidth: '16em' }}
              value={selectedTemplate?.id || ''}
              onChange={handleTemplateChange}
              label={t(translations.pages.articleMessagesSettings.templateNameLabel())}
              size="small"
            >
              {templates?.map(template => (
                <MenuItem value={template.id} key={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {renderContent(selectedTemplate)}
      </Grid>
    </DetailPage>
  );
};
